import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MenuBar from "../components/MenuBar";
import { Input, Button, MessageBox, Select, Option } from "@ui5/webcomponents-react";
import "@ui5/webcomponents/dist/Button.js";
import axios from "axios";

const ManageOrganization = () => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole");
  const defaultLogoInputRef = useRef(null);
  const activeLogoInputRef = useRef(null);
  const editDefaultLogoInputRef = useRef(null);
  const editActiveLogoInputRef = useRef(null);

  useEffect(() => {
    if (userRole !== "1" && userRole !== "9") {
      alert("You are not authorized to access this page.");
      navigate("/");
    }
  }, [userRole, navigate]);

  const [orgName, setOrgName] = useState("");
  const [orgCode, setOrgCode] = useState("");
  const [defaultLogoFile, setDefaultLogoFile] = useState(null);
  const [activeLogoFile, setActiveLogoFile] = useState(null);
  const [messageBoxProps, setMessageBoxProps] = useState(null);
  const [activeTab, setActiveTab] = useState("create");

  const [organizationsList, setOrganizationsList] = useState([]);
  const [selectedOrgCodeForEdit, setSelectedOrgCodeForEdit] = useState("");
  const [editOrgName, setEditOrgName] = useState("");
  const [editOrgCode, setEditOrgCode] = useState("");
  const [currentDefaultLogoPreview, setCurrentDefaultLogoPreview] = useState(null);
  const [currentActiveLogoPreview, setCurrentActiveLogoPreview] = useState(null);
  const [newDefaultLogoFile, setNewDefaultLogoFile] = useState(null);
  const [newActiveLogoFile, setNewActiveLogoFile] = useState(null);

  useEffect(() => {
    if (activeTab === "edit") {
      const fetchOrganizations = async () => {
        try {
          const response = await axios.get("https://icttestalarm.com:3000/api/organization/all"); // Updated endpoint
          setOrganizationsList(response.data || []);
        } catch (error) {
          console.error("Failed to fetch organizations:", error);
          setMessageBoxProps({
            open: true,
            title: "Error",
            message: "Failed to fetch organizations list.",
            onClose: () => setMessageBoxProps(null),
          });
          setOrganizationsList([]);
        }
      };
      fetchOrganizations();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedOrgCodeForEdit && activeTab === "edit") {
      const selectedOrg = organizationsList.find(org => org.organization_code === selectedOrgCodeForEdit);
      if (selectedOrg) {
        setEditOrgName(selectedOrg.organization_name);
        setEditOrgCode(selectedOrg.organization_code);
        setCurrentDefaultLogoPreview(selectedOrg.default_logo_url || null);
        setCurrentActiveLogoPreview(selectedOrg.active_logo_url || null);
        setNewDefaultLogoFile(null);
        setNewActiveLogoFile(null);
        if (editDefaultLogoInputRef.current) editDefaultLogoInputRef.current.value = null;
        if (editActiveLogoInputRef.current) editActiveLogoInputRef.current.value = null;
      }
    } else {
      setEditOrgName("");
      setEditOrgCode("");
      setCurrentDefaultLogoPreview(null);
      setCurrentActiveLogoPreview(null);
    }
  }, [selectedOrgCodeForEdit, organizationsList, activeTab]);

  const handleDefaultLogoChange = (e) => {
    setDefaultLogoFile(e.target.files[0]);
  };

  const handleActiveLogoChange = (e) => {
    setActiveLogoFile(e.target.files[0]);
  };

  const handleNewDefaultLogoChange = (e) => {
    setNewDefaultLogoFile(e.target.files[0]);
  };

  const handleNewActiveLogoChange = (e) => {
    setNewActiveLogoFile(e.target.files[0]);
  };

  const handleSubmit = async () => {
    if (!orgName || !orgCode || !defaultLogoFile || !activeLogoFile) {
      setMessageBoxProps({
        open: true,
        title: "Validation Error",
        message: "All fields are required.",
        onClose: () => setMessageBoxProps(null),
      });
      return;
    }
    const formData = new FormData();
    formData.append("organization_name", orgName);
    formData.append("organization_code", orgCode);
    formData.append("default_logo", defaultLogoFile);
    formData.append("active_logo", activeLogoFile);

    try {
      await axios.post("https://icttestalarm.com:3000/api/organization/create", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMessageBoxProps({
        open: true,
        title: "Success",
        message: "Organization created successfully!",
        onClose: () => setMessageBoxProps(null),
      });
      setOrgName("");
      setOrgCode("");
      setDefaultLogoFile(null);
      setActiveLogoFile(null);
      if (defaultLogoInputRef.current) {
        defaultLogoInputRef.current.value = null;
      }
      if (activeLogoInputRef.current) {
        activeLogoInputRef.current.value = null;
      }
    } catch (error) {
      setMessageBoxProps({
        open: true,
        title: "Error",
        message: error.response?.data?.error || "Failed to create organization.",
        onClose: () => setMessageBoxProps(null),
      });
    }
  };

  const handleUpdateSubmit = async () => {
    if (!selectedOrgCodeForEdit || !editOrgName) {
      setMessageBoxProps({
        open: true,
        title: "Validation Error",
        message: "Please select an organization and ensure name is filled.",
        onClose: () => setMessageBoxProps(null),
      });
      return;
    }

    const formData = new FormData();
    formData.append("organization_name", editOrgName);
    if (newDefaultLogoFile) {
      formData.append("default_logo", newDefaultLogoFile);
    }
    if (newActiveLogoFile) {
      formData.append("active_logo", newActiveLogoFile);
    }

    try {
      await axios.put(`https://icttestalarm.com:3000/api/organization/update/${selectedOrgCodeForEdit}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMessageBoxProps({
        open: true,
        title: "Success",
        message: "Organization updated successfully!",
        onClose: () => {
          setMessageBoxProps(null);
          setSelectedOrgCodeForEdit("");
          setActiveTab("create");
          setActiveTab("edit");
        },
      });
      setNewDefaultLogoFile(null);
      setNewActiveLogoFile(null);
      if (editDefaultLogoInputRef.current) editDefaultLogoInputRef.current.value = null;
      if (editActiveLogoInputRef.current) editActiveLogoInputRef.current.value = null;
    } catch (error) {
      setMessageBoxProps({
        open: true,
        title: "Error",
        message: error.response?.data?.error || "Failed to update organization.",
        onClose: () => setMessageBoxProps(null),
      });
    }
  };

  return (
    <div style={{ padding: "2rem" }}>
      <MenuBar />
      <h2 style={{ backgroundColor: "blue", color: "white", textAlign: "center", padding: "1rem", marginBottom: "1rem" }}>
        Manage Organization
      </h2>

      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        <Button design={activeTab === "create" ? "Emphasized" : "Default"} onClick={() => setActiveTab("create")}>
          Create Organization
        </Button>
        <Button design={activeTab === "edit" ? "Emphasized" : "Default"} onClick={() => setActiveTab("edit")}>
          Edit Organization
        </Button>
      </div>

      {activeTab === "create" && (
        <>
          <h3 style={{ marginBottom: "1rem" }}>Create Organization</h3>
          <div style={{ maxWidth: 400, display: "flex", flexDirection: "column", gap: "1rem" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ marginBottom: "0.5rem" }}>Organization Name</label>
              <Input
                placeholder="Organization Name"
                style={{ width: "100%" }}
                value={orgName}
                onInput={e => setOrgName(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ marginBottom: "0.5rem" }}>Organization Code</label>
              <Input
                placeholder="Organization Code"
                style={{ width: "100%" }}
                value={orgCode}
                onInput={e => setOrgCode(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ marginBottom: "0.5rem" }}>Default Logo (.png)</label>
              <input
                type="file"
                accept="image/png"
                onChange={handleDefaultLogoChange}
                style={{ width: "100%" }}
                ref={defaultLogoInputRef}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ marginBottom: "0.5rem" }}>Active Logo (.png)</label>
              <input
                type="file"
                accept="image/png"
                onChange={handleActiveLogoChange}
                style={{ width: "100%" }}
                ref={activeLogoInputRef}
              />
            </div>
            <div style={{ marginTop: "1rem" }}>
              <Button design="Emphasized" onClick={handleSubmit}>Create Organization</Button>
            </div>
          </div>
        </>
      )}

      {activeTab === "edit" && (
        <>
          <h3 style={{ marginBottom: "1rem" }}>Edit Organization</h3>
          <div style={{ maxWidth: 400, display: "flex", flexDirection: "column", gap: "1rem" }}>
            <label style={{ marginBottom: "0.5rem" }}>Select Organization</label>
            <Select
              style={{ width: "100%" }}
              onChange={(e) => setSelectedOrgCodeForEdit(e.detail.selectedOption.dataset.code)}
              disabled={organizationsList.length === 0}
            >
              <Option value="" data-code="">Select an Organization</Option>
              {organizationsList.map(org => (
                <Option key={org.organization_code} value={org.organization_name} data-code={org.organization_code}>
                  {org.organization_name} ({org.organization_code})
                </Option>
              ))}
            </Select>

            {selectedOrgCodeForEdit && (
              <>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: "0.5rem" }}>Organization Name</label>
                  <Input
                    placeholder="Organization Name"
                    style={{ width: "100%" }}
                    value={editOrgName}
                    onInput={e => setEditOrgName(e.target.value)}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: "0.5rem" }}>Organization Code</label>
                  <Input
                    style={{ width: "100%" }}
                    value={editOrgCode}
                    disabled
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: "0.5rem" }}>Current Default Logo</label>
                  {currentDefaultLogoPreview ? (
                    <img src={currentDefaultLogoPreview} alt="Current Default Logo" style={{ maxWidth: "100px", maxHeight: "100px", marginBottom: "0.5rem" }} />
                  ) : (<p>No default logo set or preview unavailable.</p>)}
                  <label style={{ marginBottom: "0.5rem" }}>New Default Logo (.png)</label>
                  <input
                    type="file"
                    accept="image/png"
                    onChange={handleNewDefaultLogoChange}
                    style={{ width: "100%" }}
                    ref={editDefaultLogoInputRef}
                  />
                </div>

                <div style={{ display: "flex", flexDirection: "column" }}>
                  <label style={{ marginBottom: "0.5rem" }}>Current Active Logo</label>
                  {currentActiveLogoPreview ? (
                    <img src={currentActiveLogoPreview} alt="Current Active Logo" style={{ maxWidth: "100px", maxHeight: "100px", marginBottom: "0.5rem" }} />
                  ) : (<p>No active logo set or preview unavailable.</p>)}
                  <label style={{ marginBottom: "0.5rem" }}>New Active Logo (.png)</label>
                  <input
                    type="file"
                    accept="image/png"
                    onChange={handleNewActiveLogoChange}
                    style={{ width: "100%" }}
                    ref={editActiveLogoInputRef}
                  />
                </div>
                <div style={{ marginTop: "1rem" }}>
                  <Button design="Emphasized" onClick={handleUpdateSubmit}>Update Organization</Button>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {messageBoxProps && (
        <MessageBox open={messageBoxProps.open} title={messageBoxProps.title} onClose={messageBoxProps.onClose}>
          {messageBoxProps.message}
        </MessageBox>
      )}
    </div>
  );
};

export default ManageOrganization;
